import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useMutation, useQuery } from 'react-query';
import { formatDistanceToNowStrict } from 'date-fns';
import {
  getPartnerProfile,
  updatePartnerProfile,
  listPartnerApiKeys,
  createPartnerApiKey,
  deletePartnerApiKey,
} from '../../api/partnerPortal';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';

const sanitizeList = (items = []) =>
  Array.from(
    new Set(
      (items || []).map((item) => (typeof item === 'string' ? item.trim() : String(item).trim()))
    )
  ).filter(Boolean);

const numberOrUndefined = (value) => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const formatRelativeTime = (value) => {
  if (!value) return '—';
  try {
    return formatDistanceToNowStrict(new Date(value), { addSuffix: true });
  } catch {
    return '—';
  }
};

const buildFormState = (profile) => ({
  company_info: {
    legalName: profile?.company_info?.legalName || '',
    doingBusinessAs: profile?.company_info?.doingBusinessAs || '',
    website: profile?.company_info?.website || '',
    address: {
      street: profile?.company_info?.address?.street || '',
      city: profile?.company_info?.address?.city || '',
      state: profile?.company_info?.address?.state || '',
      postalCode: profile?.company_info?.address?.postalCode || '',
      country: profile?.company_info?.address?.country || '',
    },
    contacts: {
      primary: {
        name: profile?.company_info?.contacts?.primary?.name || '',
        email: profile?.company_info?.contacts?.primary?.email || '',
        phone: profile?.company_info?.contacts?.primary?.phone || '',
      },
      operations: {
        name: profile?.company_info?.contacts?.operations?.name || '',
        email: profile?.company_info?.contacts?.operations?.email || '',
        phone: profile?.company_info?.contacts?.operations?.phone || '',
      },
      finance: {
        name: profile?.company_info?.contacts?.finance?.name || '',
        email: profile?.company_info?.contacts?.finance?.email || '',
        phone: profile?.company_info?.contacts?.finance?.phone || '',
      },
    },
  },
  capabilities: sanitizeList(profile?.capabilities),
  equipment: sanitizeList(profile?.equipment),
  certifications: sanitizeList(profile?.certifications),
  production_capacity: {
    minUnits:
      profile?.production_capacity?.minUnits !== undefined
        ? String(profile.production_capacity.minUnits)
        : '',
    maxUnits:
      profile?.production_capacity?.maxUnits !== undefined
        ? String(profile.production_capacity.maxUnits)
        : '',
    monthlyCapacity:
      profile?.production_capacity?.monthlyCapacity !== undefined
        ? String(profile.production_capacity.monthlyCapacity)
        : '',
    notes: profile?.production_capacity?.notes || '',
  },
  operational_profile: {
    shippingRadius: profile?.operational_profile?.shippingRadius || '',
    supportedMaterials: sanitizeList(profile?.operational_profile?.supportedMaterials),
    garmentTypes: sanitizeList(profile?.operational_profile?.garmentTypes),
    leadTimes: {
      standard:
        profile?.operational_profile?.leadTimes?.standard !== undefined
          ? String(profile.operational_profile.leadTimes.standard)
          : '',
      rush:
        profile?.operational_profile?.leadTimes?.rush !== undefined
          ? String(profile.operational_profile.leadTimes.rush)
          : '',
    },
    qualityMetrics: {
      defectRate:
        profile?.operational_profile?.qualityMetrics?.defectRate !== undefined
          ? String(profile.operational_profile.qualityMetrics.defectRate)
          : '',
      onTimeDelivery:
        profile?.operational_profile?.qualityMetrics?.onTimeDelivery !== undefined
          ? String(profile.operational_profile.qualityMetrics.onTimeDelivery)
          : '',
    },
  },
});

const EditableChipInput = ({
  label,
  helperText,
  values,
  onAdd,
  onRemove,
  placeholder,
  disabled,
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setInputValue('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      handleAdd();
    }
  };

  return (
    <Box>
      <TextField
        label={label}
        value={inputValue}
        onChange={(event) => setInputValue(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (inputValue.trim()) {
            handleAdd();
          }
        }}
        placeholder={placeholder}
        helperText={helperText || 'Press Enter to add items to the list.'}
        fullWidth
        disabled={disabled}
      />
      <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
        {(values || []).map((value) => (
          <Chip
            key={value}
            label={value}
            onDelete={disabled ? undefined : () => onRemove(value)}
            sx={{ mb: 1 }}
          />
        ))}
      </Stack>
    </Box>
  );
};

const PartnerProfilePage = () => {
  const { partner, syncProfile, token } = usePartnerAuth();
  const {
    data: profileData,
    isLoading,
    isFetching,
    refetch,
  } = useQuery(['partner-profile'], getPartnerProfile, {
    initialData: partner?.profile,
    onSuccess: (data) => {
      if (data) {
        syncProfile?.(data);
      }
    },
    staleTime: 1000 * 60,
  });

  const {
    data: apiKeysData,
    isLoading: isLoadingApiKeys,
    isFetching: isFetchingApiKeys,
    refetch: refetchApiKeys,
  } = useQuery(['partner-api-keys'], listPartnerApiKeys, {
    enabled: !!token,
    refetchOnWindowFocus: false,
  });

  const apiKeys = apiKeysData || [];

  const [formData, setFormData] = useState(() => buildFormState(profileData));
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [newApiKeyLabel, setNewApiKeyLabel] = useState('');
  const [newApiKeyValue, setNewApiKeyValue] = useState(null);
  const [apiKeyError, setApiKeyError] = useState('');

  useEffect(() => {
    if (profileData) {
      setFormData(buildFormState(profileData));
    }
  }, [profileData]);

  const updateProfile = useMutation(updatePartnerProfile, {
    onSuccess: (profile) => {
      setSuccessMessage('Profile updated successfully.');
      setErrorMessage('');
      syncProfile?.(profile);
      refetch();
    },
    onError: (err) => {
      setSuccessMessage('');
      setErrorMessage(
        err?.response?.data?.error ||
          err?.message ||
          'Unable to update profile. Please try again.'
      );
    },
  });

  const createApiKey = useMutation(createPartnerApiKey, {
    onSuccess: (data) => {
      setNewApiKeyValue(data.apiKey);
      setApiKeyError('');
      setNewApiKeyLabel('');
      refetchApiKeys();
    },
    onError: (err) => {
      setApiKeyError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to generate API key. Please try again.'
      );
    },
  });

  const deleteApiKey = useMutation(deletePartnerApiKey, {
    onSuccess: () => {
      refetchApiKeys();
    },
    onError: (err) => {
      setApiKeyError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to delete API key. Please try again.'
      );
    },
  });

  const performanceMetrics = useMemo(
    () => partner?.profile?.performance_metrics || profileData?.performance_metrics,
    [partner?.profile?.performance_metrics, profileData?.performance_metrics]
  );

  const handleGenerateApiKey = () => {
    if (createApiKey.isLoading) return;
    setApiKeyError('');
    createApiKey.mutate({
      label: newApiKeyLabel || undefined,
    });
  };

  const handleDeleteApiKey = ({ tokenId, tokenHash }) => {
    if (deleteApiKey.isLoading) return;
    setApiKeyError('');
    const identifier = tokenId || tokenHash;
    if (!identifier) return;
    deleteApiKey.mutate({
      tokenId: identifier,
      tokenHash,
    });
  };

  const handleCompanyFieldChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      company_info: {
        ...prev.company_info,
        [field]: event.target.value,
      },
    }));
  };

  const handleAddressChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      company_info: {
        ...prev.company_info,
        address: {
          ...prev.company_info.address,
          [field]: event.target.value,
        },
      },
    }));
  };

  const handleContactChange = (role, field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      company_info: {
        ...prev.company_info,
        contacts: {
          ...prev.company_info.contacts,
          [role]: {
            ...prev.company_info.contacts[role],
            [field]: event.target.value,
          },
        },
      },
    }));
  };

  const handleCapacityChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      production_capacity: {
        ...prev.production_capacity,
        [field]: event.target.value,
      },
    }));
  };

  const handleOperationalFieldChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      operational_profile: {
        ...prev.operational_profile,
        [field]: event.target.value,
      },
    }));
  };

  const handleLeadTimeChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      operational_profile: {
        ...prev.operational_profile,
        leadTimes: {
          ...prev.operational_profile.leadTimes,
          [field]: event.target.value,
        },
      },
    }));
  };

  const handleQualityMetricChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      operational_profile: {
        ...prev.operational_profile,
        qualityMetrics: {
          ...prev.operational_profile.qualityMetrics,
          [field]: event.target.value,
        },
      },
    }));
  };

  const arrayHandlers = {
    addCapability: (value) =>
      setFormData((prev) => ({
        ...prev,
        capabilities: sanitizeList([...prev.capabilities, value]),
      })),
    removeCapability: (value) =>
      setFormData((prev) => ({
        ...prev,
        capabilities: prev.capabilities.filter((item) => item !== value),
      })),
    addEquipment: (value) =>
      setFormData((prev) => ({
        ...prev,
        equipment: sanitizeList([...prev.equipment, value]),
      })),
    removeEquipment: (value) =>
      setFormData((prev) => ({
        ...prev,
        equipment: prev.equipment.filter((item) => item !== value),
      })),
    addCertification: (value) =>
      setFormData((prev) => ({
        ...prev,
        certifications: sanitizeList([...prev.certifications, value]),
      })),
    removeCertification: (value) =>
      setFormData((prev) => ({
        ...prev,
        certifications: prev.certifications.filter((item) => item !== value),
      })),
    addMaterial: (value) =>
      setFormData((prev) => ({
        ...prev,
        operational_profile: {
          ...prev.operational_profile,
          supportedMaterials: sanitizeList([...prev.operational_profile.supportedMaterials, value]),
        },
      })),
    removeMaterial: (value) =>
      setFormData((prev) => ({
        ...prev,
        operational_profile: {
          ...prev.operational_profile,
          supportedMaterials: prev.operational_profile.supportedMaterials.filter(
            (item) => item !== value
          ),
        },
      })),
    addGarmentType: (value) =>
      setFormData((prev) => ({
        ...prev,
        operational_profile: {
          ...prev.operational_profile,
          garmentTypes: sanitizeList([...prev.operational_profile.garmentTypes, value]),
        },
      })),
    removeGarmentType: (value) =>
      setFormData((prev) => ({
        ...prev,
        operational_profile: {
          ...prev.operational_profile,
          garmentTypes: prev.operational_profile.garmentTypes.filter((item) => item !== value),
        },
      })),
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    const payload = {
      company_info: {
        legalName: formData.company_info.legalName || undefined,
        doingBusinessAs: formData.company_info.doingBusinessAs || undefined,
        website: formData.company_info.website || undefined,
        address: {
          street: formData.company_info.address.street || undefined,
          city: formData.company_info.address.city || undefined,
          state: formData.company_info.address.state || undefined,
          postalCode: formData.company_info.address.postalCode || undefined,
          country: formData.company_info.address.country || undefined,
        },
        contacts: {
          primary: {
            name: formData.company_info.contacts.primary.name || undefined,
            email: formData.company_info.contacts.primary.email || undefined,
            phone: formData.company_info.contacts.primary.phone || undefined,
          },
          operations: {
            name: formData.company_info.contacts.operations.name || undefined,
            email: formData.company_info.contacts.operations.email || undefined,
            phone: formData.company_info.contacts.operations.phone || undefined,
          },
          finance: {
            name: formData.company_info.contacts.finance.name || undefined,
            email: formData.company_info.contacts.finance.email || undefined,
            phone: formData.company_info.contacts.finance.phone || undefined,
          },
        },
      },
      capabilities: sanitizeList(formData.capabilities),
      equipment: sanitizeList(formData.equipment),
      certifications: sanitizeList(formData.certifications),
      production_capacity: {
        minUnits: numberOrUndefined(formData.production_capacity.minUnits),
        maxUnits: numberOrUndefined(formData.production_capacity.maxUnits),
        monthlyCapacity: numberOrUndefined(formData.production_capacity.monthlyCapacity),
        notes: formData.production_capacity.notes || undefined,
      },
      operational_profile: {
        shippingRadius: formData.operational_profile.shippingRadius || undefined,
        supportedMaterials: sanitizeList(formData.operational_profile.supportedMaterials),
        garmentTypes: sanitizeList(formData.operational_profile.garmentTypes),
        leadTimes: {
          standard: numberOrUndefined(formData.operational_profile.leadTimes.standard),
          rush: numberOrUndefined(formData.operational_profile.leadTimes.rush),
        },
        qualityMetrics: {
          defectRate: numberOrUndefined(formData.operational_profile.qualityMetrics.defectRate),
          onTimeDelivery: numberOrUndefined(
            formData.operational_profile.qualityMetrics.onTimeDelivery
          ),
        },
      },
    };

    updateProfile.mutate(payload);
  };

  if (isLoading && !profileData) {
    return (
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gap: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Organization Profile
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Keep your capabilities, key contacts, and operational profile current so Studio Sivad
            can route the right campaigns to your team.
          </Typography>
        </Box>
        {isFetching && <CircularProgress size={20} />}
      </Stack>

      {successMessage && <Alert severity="success">{successMessage}</Alert>}
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Company Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Legal name"
              value={formData.company_info.legalName}
              onChange={handleCompanyFieldChange('legalName')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Doing business as"
              value={formData.company_info.doingBusinessAs}
              onChange={handleCompanyFieldChange('doingBusinessAs')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Website"
              value={formData.company_info.website}
              onChange={handleCompanyFieldChange('website')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>
              Headquarters Address
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Street"
              value={formData.company_info.address.street}
              onChange={handleAddressChange('street')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="City"
              value={formData.company_info.address.city}
              onChange={handleAddressChange('city')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="State / Province"
              value={formData.company_info.address.state}
              onChange={handleAddressChange('state')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Postal code"
              value={formData.company_info.address.postalCode}
              onChange={handleAddressChange('postalCode')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Country"
              value={formData.company_info.address.country}
              onChange={handleAddressChange('country')}
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Key Contacts
        </Typography>
        <Grid container spacing={2}>
          {['primary', 'operations', 'finance'].map((role) => (
            <Grid item xs={12} md={4} key={role}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', mb: 1 }}>
                  {role} contact
                </Typography>
                <Stack spacing={1.5}>
                  <TextField
                    label="Name"
                    value={formData.company_info.contacts[role].name}
                    onChange={handleContactChange(role, 'name')}
                    fullWidth
                  />
                  <TextField
                    label="Email"
                    value={formData.company_info.contacts[role].email}
                    onChange={handleContactChange(role, 'email')}
                    fullWidth
                  />
                  <TextField
                    label="Phone"
                    value={formData.company_info.contacts[role].phone}
                    onChange={handleContactChange(role, 'phone')}
                    fullWidth
                  />
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              height: '100%',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Production Capacity
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Minimum units"
                  value={formData.production_capacity.minUnits}
                  onChange={handleCapacityChange('minUnits')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Maximum units"
                  value={formData.production_capacity.maxUnits}
                  onChange={handleCapacityChange('maxUnits')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Monthly capacity"
                  value={formData.production_capacity.monthlyCapacity}
                  onChange={handleCapacityChange('monthlyCapacity')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Capacity notes"
                  value={formData.production_capacity.notes}
                  onChange={handleCapacityChange('notes')}
                  multiline
                  minRows={3}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              height: '100%',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Capabilities & Equipment
            </Typography>
            <Stack spacing={2}>
              <EditableChipInput
                label="Capabilities"
                values={formData.capabilities}
                onAdd={arrayHandlers.addCapability}
                onRemove={arrayHandlers.removeCapability}
                placeholder="Add capability (e.g. digital cutting)"
              />
              <EditableChipInput
                label="Equipment"
                values={formData.equipment}
                onAdd={arrayHandlers.addEquipment}
                onRemove={arrayHandlers.removeEquipment}
                placeholder="Add equipment"
              />
              <EditableChipInput
                label="Certifications"
                values={formData.certifications}
                onAdd={arrayHandlers.addCertification}
                onRemove={arrayHandlers.removeCertification}
                placeholder="Add certification"
              />
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Operational Profile
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Shipping radius (km)"
              value={formData.operational_profile.shippingRadius}
              onChange={handleOperationalFieldChange('shippingRadius')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Standard lead time (days)"
              value={formData.operational_profile.leadTimes.standard}
              onChange={handleLeadTimeChange('standard')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Rush lead time (days)"
              value={formData.operational_profile.leadTimes.rush}
              onChange={handleLeadTimeChange('rush')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <EditableChipInput
              label="Supported materials"
              values={formData.operational_profile.supportedMaterials}
              onAdd={arrayHandlers.addMaterial}
              onRemove={arrayHandlers.removeMaterial}
              placeholder="Add material"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <EditableChipInput
              label="Garment types"
              values={formData.operational_profile.garmentTypes}
              onAdd={arrayHandlers.addGarmentType}
              onRemove={arrayHandlers.removeGarmentType}
              placeholder="Add garment type"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Quality defect rate (%)"
              value={formData.operational_profile.qualityMetrics.defectRate}
              onChange={handleQualityMetricChange('defectRate')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="On-time delivery (%)"
              value={formData.operational_profile.qualityMetrics.onTimeDelivery}
              onChange={handleQualityMetricChange('onTimeDelivery')}
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            API Keys
          </Typography>
          {isFetchingApiKeys && <CircularProgress size={18} />}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Generate API keys for secure programmatic access to Studio Sivad services. Treat each key
          like a password and revoke any that are no longer needed.
        </Typography>

        {newApiKeyValue && (
          <Alert
            severity="success"
            sx={{ mt: 2 }}
            onClose={() => setNewApiKeyValue(null)}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              New API key generated
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Copy and store this key now. It will not be shown again.
            </Typography>
            <Box
              component="code"
              sx={{
                mt: 1,
                display: 'block',
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'grey.900',
                color: 'grey.100',
                wordBreak: 'break-all',
                fontFamily: 'monospace',
              }}
            >
              {newApiKeyValue}
            </Box>
          </Alert>
        )}

        {apiKeyError && (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            onClose={() => setApiKeyError('')}
          >
            {apiKeyError}
          </Alert>
        )}

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'flex-end' }}
          sx={{ mt: 3 }}
        >
          <TextField
            label="Key label (optional)"
            value={newApiKeyLabel}
            onChange={(event) => setNewApiKeyLabel(event.target.value)}
            placeholder="e.g. Production integration"
            fullWidth
          />
          <Button
            variant="contained"
            onClick={handleGenerateApiKey}
            disabled={createApiKey.isLoading}
            startIcon={
              createApiKey.isLoading ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {createApiKey.isLoading ? 'Generating...' : 'Generate API key'}
          </Button>
        </Stack>

        {isLoadingApiKeys ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : apiKeys.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            No API keys have been created yet. Generate the first key above to connect your internal
            systems programmatically.
          </Typography>
        ) : (
          <Stack spacing={1.5} sx={{ mt: 3 }}>
            {apiKeys.map((token) => (
              <Paper
                key={token.id || token.tokenHash}
                variant="outlined"
                sx={{ p: 2, borderRadius: 2 }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  spacing={1.5}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {token.label || 'API Key'}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                      {token.lastFour && (
                        <Chip label={`•••• ${token.lastFour}`} size="small" variant="outlined" />
                      )}
                      <Chip
                        label={token.accessLevel || 'standard'}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Created {formatRelativeTime(token.createdAt)} • Usage count:{' '}
                      {token.usageCount ?? 0}
                    </Typography>
                    {token.lastUsedAt && (
                      <Typography variant="caption" color="text.secondary">
                        Last used {formatRelativeTime(token.lastUsedAt)}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    variant="text"
                    color="error"
                    disabled={deleteApiKey.isLoading || (!token.id && !token.tokenHash)}
                    onClick={() =>
                      handleDeleteApiKey({
                        tokenId: token.id,
                        tokenHash: token.tokenHash,
                      })
                    }
                  >
                    {deleteApiKey.isLoading ? 'Removing...' : 'Revoke'}
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      {performanceMetrics && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Performance Snapshot
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Projects completed
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {performanceMetrics.projectsCompleted ?? 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Average quality score
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {performanceMetrics.averageQualityScore ?? '—'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  On-time delivery
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {performanceMetrics.onTimeDeliveryRate ?? '—'}%
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Total production volume
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {performanceMetrics.totalProductionVolume ?? '—'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Divider />

      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="flex-end" spacing={2}>
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={updateProfile.isLoading}
        >
          {updateProfile.isLoading ? 'Saving...' : 'Save profile'}
        </Button>
      </Stack>
    </Box>
  );
};

export default PartnerProfilePage;
